import * as msal from '@azure/msal-browser';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-client';

/**
 * Creates an MSAL configuration object for Microsoft authentication
 * @param clientId - The Azure AD client ID
 * @param tenantId - The Azure AD tenant ID
 * @param redirectUri - The OAuth redirect URI
 * @returns MSAL configuration object
 */
export function createMsalConfig(
  clientId: string,
  tenantId: string,
  redirectUri: string,
): msal.Configuration {
  return {
    auth: {
      clientId: clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: redirectUri,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };
}

/**
 * Creates a Microsoft Graph client with authentication
 * Automatically handles MSAL initialization and token refresh with interactive fallback
 * @param msalInstance - The MSAL instance to use for authentication
 * @param scopes - The OAuth scopes required for the Graph API calls
 * @returns A configured Microsoft Graph client
 */
export function createGraphClient(
  msalInstance: msal.PublicClientApplication,
  scopes: string[],
): MicrosoftGraph.Client {
  return MicrosoftGraph.Client.init({
    authProvider: async (done) => {
      try {
        // Ensure MSAL is initialized before token acquisition
        await msalInstance.initialize();

        // Get the active account or first available account
        const account =
          msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

        if (!account) {
          throw new Error('No account found - user must log in first');
        }

        // Try silent token acquisition first with the specific account
        const accessToken = await msalInstance.acquireTokenSilent({
          scopes,
          account,
        });
        done(null, accessToken.accessToken);
      } catch {
        // If silent acquisition fails, try interactive popup
        try {
          const interactiveToken = await msalInstance.acquireTokenPopup({
            scopes,
          });
          done(null, interactiveToken.accessToken);
        } catch (interactiveError) {
          // Both silent and interactive failed - pass error to Graph client
          done(interactiveError, null);
        }
      }
    },
  });
}

/**
 * Checks if the user is already authenticated without showing a popup
 * @param msalInstance - The MSAL instance to check authentication for
 * @param scopes - The OAuth scopes to check
 * @returns True if authenticated, false otherwise
 */
export async function checkAuthentication(
  msalInstance: msal.PublicClientApplication,
  scopes: string[],
): Promise<boolean> {
  try {
    await msalInstance.initialize();

    // Get the active account or first available account
    const account =
      msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

    if (!account) {
      return false;
    }

    await msalInstance.acquireTokenSilent({ scopes, account });

    return true;
  } catch {
    return false;
  }
}

/**
 * Performs Microsoft login with popup
 * @param msalInstance - The MSAL instance to use for login
 * @param scopes - The OAuth scopes to request
 */
export async function login(
  msalInstance: msal.PublicClientApplication,
  scopes: string[],
): Promise<void> {
  try {
    await msalInstance.initialize();
    try {
      await msalInstance.acquireTokenSilent({ scopes });
    } catch {
      const resp = await msalInstance.loginPopup({ scopes });
      await msalInstance.setActiveAccount(resp.account);
    }
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Strips HTML tags and decodes common entities to produce a plain-text string,
 * for plain-text destinations like the MS To-Do / Planner task body. AI-generated
 * todo descriptions are HTML; rendering them verbatim in a text field would show
 * raw tags.
 * @param html - The HTML string (may be null/undefined)
 * @returns Plain-text rendering, or an empty string for empty input
 */
export function htmlToPlainText(html: string | null | undefined): string {
  if (!html) {
    return '';
  }

  return html
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<(br|hr)\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

/**
 * Generates a simple GUID (UUID v4)
 * @returns A GUID string in the format xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
export function generateGUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;

    return v.toString(16);
  });
}

/**
 * Extracts the refresh token from MSAL's browser cache.
 * Note: This accesses MSAL's internal cache structure which may change between versions.
 * Requires 'offline_access' scope to be included in the token request.
 *
 * @param clientId - The Azure AD client ID used for authentication
 * @param cacheLocation - Where MSAL stores tokens ('sessionStorage' or 'localStorage')
 * @returns The refresh token if found, null otherwise
 */
export function getRefreshTokenFromCache(
  clientId: string,
  cacheLocation: 'sessionStorage' | 'localStorage' = 'sessionStorage',
): string | null {
  const storage =
    cacheLocation === 'sessionStorage' ? sessionStorage : localStorage;

  // MSAL stores refresh tokens with keys containing the credential type 'refreshtoken'
  // Key format: {homeAccountId}-{environment}-refreshtoken-{clientId}--
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (
      key &&
      key.toLowerCase().includes('refreshtoken') &&
      key.toLowerCase().includes(clientId.toLowerCase())
    ) {
      try {
        const value = storage.getItem(key);
        if (value) {
          const parsed = JSON.parse(value);
          if (parsed.secret) {
            return parsed.secret;
          }
        }
      } catch {
        // Skip invalid JSON entries
        continue;
      }
    }
  }

  return null;
}
